import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Grid, InputAdornment, TextField } from "@material-ui/core";
import { fade, makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import { Autocomplete } from "@material-ui/lab";
import { useIntl } from "react-intl";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { ApplicationState } from "../store";
import { ProductsData } from "../models/types";
import { searchText } from "../store/products/action";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      position: "relative",
      flexGrow: 1,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: fade(theme.palette.common.white, 0.15),
      marginRight: "2px",
      width: "100%",
      "&:hover": {
        backgroundColor: fade(theme.palette.common.white, 0.25),
      },
      [theme.breakpoints.up("sm")]: {
        marginLeft: theme.spacing(3),
        width: "auto",
      },
    },
    mobileRoot: {
      flexGrow: 1,
      minWidth: 0,
      width: "100%",
    },
    inputRoot: {
      padding: "0px",
      margin: "2px",
    },
    inputInput: {
      height: "35px",
      color: "inherit",
      padding: "0px",
      border: "1px solid #DA2232",
      backgroundColor: "#ffffff",
      "& .MuiInputLabel-outlined": {
        transform: "translate(14px, 12px) scale(1)",
        color: "#333333",
        fontStyle: "italic",
      },
    },
    searchContent: {
      color: "#333333",
      border: "0px",
      minHeight: "34px",
      minWidth: "180px",
      borderRadius: "0px",
      textDecoration: "none",
      textAlign: "left",
    },
    searchImage: {
      width: "80px",
      textAlign: "center",
    },
    searchImageDetails: {
      maxWidth: "50px",
      maxHeight: "34px",
    },
    resultLink: {
      color: "inherit",
      textDecoration: "none",
      width: "100%",
    },
  })
);

interface OwnProps {
  mobile?: boolean;
  autoFocus?: boolean;
  onResultSelected?: () => void;
}

interface PropsFromState {
  searchResults: ProductsData[];
}

interface PropsFromDispatch {
  searchText: (text: string) => any;
}

type Props = OwnProps & PropsFromState & PropsFromDispatch;

const ProductSearch: React.FC<Props> = ({
  mobile = false,
  autoFocus = false,
  onResultSelected,
  searchResults,
  searchText,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<ProductsData[]>([]);

  useEffect(() => {
    setOptions(searchResults);
  }, [searchResults]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearch(value);
    if (value.length > 1) {
      searchText(value);
    } else {
      setOptions([]);
    }
  };

  return (
    <div className={mobile ? classes.mobileRoot : classes.root}>
      <Autocomplete
        id={mobile ? "mobile-product-search" : "product-search"}
        className={classes.inputRoot}
        options={search.length > 1 ? options : []}
        autoComplete
        includeInputInList
        noOptionsText={intl.formatMessage({ id: "NOT_FOUND" })}
        filterSelectedOptions
        onChange={(_event: any, newValue: ProductsData | null) => {
          setOptions(newValue ? [newValue, ...options] : options);
        }}
        getOptionLabel={(option) => option?.title["lv"]}
        renderOption={(option) => {
          const product = option.searchType === "P";
          const target = product
            ? "/product/" + option?.code
            : "/product/category/" + option?.code;
          const image = product
            ? process.env.REACT_APP_IMAGE_HOST +
              "/files/product_images/" +
              (option?.image?.[0]?.img || "")
            : process.env.REACT_APP_IMAGE_HOST +
              "/files/menu_pictures/" +
              (option?.image?.[0]?.img || "");

          return (
            <Link
              to={target}
              className={classes.resultLink}
              onClick={onResultSelected}
            >
              <Grid container alignItems="center">
                <Grid item xs={3} className={classes.searchImage}>
                  <img
                    src={image}
                    alt={
                      product
                        ? option?.image?.[0]?.description
                        : option?.title["lv"]
                    }
                    className={classes.searchImageDetails}
                  />
                </Grid>
                <Grid item xs={9} className={classes.searchContent}>
                  {option?.title["lv"]}
                </Grid>
              </Grid>
            </Link>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus={autoFocus}
            variant="outlined"
            onChange={handleSearch}
            className={classes.inputInput}
            fullWidth
            label={
              search === ""
                ? intl.formatMessage({ id: "searchProducts" })
                : ""
            }
            InputLabelProps={{ shrink: false }}
            InputProps={{
              ...params.InputProps,
              endAdornment: <InputAdornment position="end" />,
            }}
          />
        )}
      />
    </div>
  );
};

const mapStateToProps = ({ products }: ApplicationState) => ({
  searchResults: products.searchResults,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  searchText: (text: string) => dispatch(searchText(text)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductSearch);
